import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import * as settingsApi from '@/api/settings'
import { useAuth } from '@/auth/AuthContext'
import { SettingsShell } from '@/components/SettingsShell'
import {
  AI_PROVIDERS,
  CUSTOM_MODEL,
  modelSelectValue,
  providerMeta,
  resolveModelId,
} from '@/lib/aiModels'
import { getRememberedSettingsCustomer } from '@/settingsContext'

export function AiSettings() {
  const { isAdmin } = useAuth()
  const [customerId] = useState(() => getRememberedSettingsCustomer())
  const [provider, setProvider] = useState('openai')
  const [model, setModel] = useState(AI_PROVIDERS[0].defaultModel)
  const [modelChoice, setModelChoice] = useState(AI_PROVIDERS[0].defaultModel)
  const [apiKey, setApiKey] = useState('')
  const [hasKey, setHasKey] = useState(false)
  const [keyHint, setKeyHint] = useState(null)
  const [configured, setConfigured] = useState(false)
  const [playbook, setPlaybook] = useState('')
  const [defaultPlaybook, setDefaultPlaybook] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [clearKey, setClearKey] = useState(false)

  function applyModelState(nextProvider, nextModel) {
    const meta = providerMeta(nextProvider)
    const resolved = resolveModelId(nextModel) || meta.defaultModel
    setProvider(nextProvider)
    setModel(resolved)
    setModelChoice(modelSelectValue(nextProvider, resolved))
  }

  useEffect(() => {
    if (!isAdmin) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await settingsApi.getAiSettings()
        if (cancelled) return
        const p = data.provider || 'openai'
        applyModelState(p, data.model)
        setHasKey(Boolean(data.has_api_key))
        setKeyHint(data.api_key_hint || null)
        setConfigured(Boolean(data.configured))
        const builtIn = data.default_playbook || ''
        setDefaultPlaybook(builtIn)
        setPlaybook((data.playbook || '').trim() || builtIn)
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load AI settings.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [isAdmin])

  if (!isAdmin) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSaved(false)
    const effectiveModel =
      modelChoice === CUSTOM_MODEL ? model.trim() : modelChoice
    if (!effectiveModel) {
      setError('Choose a model or enter a custom model id.')
      setSaving(false)
      return
    }
    try {
      const trimmedPlaybook = playbook.trim()
      const payload = {
        provider,
        model: effectiveModel,
        playbook:
          trimmedPlaybook === (defaultPlaybook || '').trim()
            ? ''
            : trimmedPlaybook,
      }
      if (clearKey) {
        payload.api_key = ''
      } else if (apiKey.trim()) {
        payload.api_key = apiKey.trim()
      }
      const updated = await settingsApi.updateAiSettings(payload)
      setHasKey(Boolean(updated.has_api_key))
      setKeyHint(updated.api_key_hint || null)
      setConfigured(Boolean(updated.configured))
      applyModelState(updated.provider || provider, updated.model || effectiveModel)
      const builtIn = updated.default_playbook || defaultPlaybook
      setDefaultPlaybook(builtIn)
      setPlaybook((updated.playbook || '').trim() || builtIn)
      setApiKey('')
      setClearKey(false)
      setSaved(true)
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not save AI settings.',
      )
    } finally {
      setSaving(false)
    }
  }

  const meta = providerMeta(provider)

  return (
    <SettingsShell title="AI" customerId={customerId || undefined}>
      <div className="space-y-6">
        <div className="rounded-xl border border-slate-200 bg-white/90 px-8 py-8 shadow-sm">
          <h1 className="font-display text-4xl tracking-tight text-brand">AI</h1>
          <p className="mt-2 max-w-2xl text-sm text-brand-muted">
            Choose an AI provider, model and API key (encrypted on this server)
            for admin <Link to="/ai-search" className="underline">AI Search</Link>.
            Password fields are never sent to the model. You can also pick a
            different model per search without changing the default here.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {saved && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            AI settings saved.
            {configured
              ? ' AI Search is ready to use.'
              : ' Add a provider and API key to enable search.'}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-4 rounded-xl border border-slate-200 bg-white/90 p-6 shadow-sm"
        >
          {loading ? (
            <p className="text-sm text-brand-muted">Loading…</p>
          ) : (
            <>
              <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                Status:{' '}
                <strong className={configured ? 'text-emerald-800' : 'text-amber-800'}>
                  {configured ? 'Configured' : 'Not configured'}
                </strong>
                {hasKey && keyHint && (
                  <span className="ml-2 text-brand-muted">Key {keyHint}</span>
                )}
              </div>

              <label className="block max-w-md text-sm">
                <span className="text-brand-muted">Provider</span>
                <select
                  value={provider}
                  onChange={(e) => {
                    const next = e.target.value
                    applyModelState(next, providerMeta(next).defaultModel)
                  }}
                  className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
                >
                  {AI_PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs text-brand-muted">{meta.hint}</span>
              </label>

              <label className="block max-w-md text-sm">
                <span className="text-brand-muted">Default model</span>
                <select
                  value={modelChoice}
                  onChange={(e) => {
                    const choice = e.target.value
                    setModelChoice(choice)
                    if (choice !== CUSTOM_MODEL) {
                      setModel(choice)
                    }
                  }}
                  className="mt-1 w-full rounded border border-slate-300 bg-white px-3 py-2"
                >
                  {meta.models.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                  <option value={CUSTOM_MODEL}>Custom model id…</option>
                </select>
              </label>

              {modelChoice === CUSTOM_MODEL && (
                <label className="block max-w-md text-sm">
                  <span className="text-brand-muted">Custom model id</span>
                  <input
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                    placeholder={meta.defaultModel}
                    required
                  />
                </label>
              )}

              <label className="block max-w-lg text-sm">
                <span className="text-brand-muted">
                  API key {hasKey ? '(leave blank to keep current)' : ''}
                </span>
                <input
                  type="password"
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value)
                    if (e.target.value) setClearKey(false)
                  }}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm"
                  autoComplete="off"
                  placeholder={hasKey ? '••••••••' : 'Paste API key'}
                  disabled={clearKey}
                />
              </label>

              {hasKey && (
                <label className="flex items-center gap-2 text-sm text-brand">
                  <input
                    type="checkbox"
                    checked={clearKey}
                    onChange={(e) => {
                      setClearKey(e.target.checked)
                      if (e.target.checked) setApiKey('')
                    }}
                  />
                  Remove stored API key
                </label>
              )}

              <label className="block text-sm">
                <span className="text-brand-muted">Search playbook</span>
                <textarea
                  value={playbook}
                  onChange={(e) => setPlaybook(e.target.value)}
                  rows={14}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs leading-relaxed"
                  spellCheck={false}
                />
                <span className="mt-1 block text-xs text-brand-muted">
                  Must include the {'{context}'} placeholder where knowledge-base
                  excerpts are inserted. Leave as the default for summary + table
                  answers with Copy for Excel.
                </span>
                {defaultPlaybook && playbook.trim() !== defaultPlaybook.trim() && (
                  <button
                    type="button"
                    className="mt-2 text-xs font-medium underline"
                    onClick={() => setPlaybook(defaultPlaybook)}
                  >
                    Reset to default playbook
                  </button>
                )}
              </label>

              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save AI settings'}
              </button>
            </>
          )}
        </form>
      </div>
    </SettingsShell>
  )
}
